import React, {
	forwardRef,
	useImperativeHandle,
	useEffect,
	useState,
	Fragment,
} from 'react';
import OverlayLoading from '../OverlayLoading';
import NavigationStepperItems, {
	NavigationStepperItemsProps,
} from './NavigationStepperItems';
import useNavigationStepper from './useNavigationStepper';

// @ts-ignore
import styles from './NavigationStepper.module.scss';

export interface NavigationStepperRef {
	setActiveStepPath: (path: string) => void;
}

export interface NavigationStepperProps {
	steps: NavigationStepperItemType[];
	initialStepPath?: string;
	onDone?: () => void;
	canSkip?: boolean;
	renderItems?: (params: NavigationStepperItemsProps) => React.ReactElement;
	onStepChange?: (stepPath: string) => void;
	showStepNumbers?: boolean;
	showStepNavigation?: boolean;
	navigationClassName?: string;
}

export type NavigationStepperItemType =
	| ComponentNavigationStepperItem
	| ClickNavigationStepperItem
	| ComponentButtonNavigationStepperItem;

interface BaseNavigationStepperItem {
	key: string;
	name: string;
	children?: NavigationStepperItemType[];
	disabled?: boolean;
	fullStepKey?: string;
	prevStepKey?: string;
	nextStepKey?: string;
	cantGoBack?: boolean;
	tooltip?: string | null;
	icon?: string;
	/** Count shown next to the name (e.g. new received items); hidden when 0. */
	badge?: number;
}

interface ComponentNavigationStepperItem extends BaseNavigationStepperItem {
	component: (params: NavigationStepperComponentParams) => React.ReactElement;
	onStepChange?: () => void;
}

interface ClickNavigationStepperItem extends BaseNavigationStepperItem {
	onClick: () => void;
}

interface ComponentButtonNavigationStepperItem
	extends BaseNavigationStepperItem {
	renderButton: () => React.ReactElement;
	onClick: () => void;
}

export interface NavigationStepperComponentParams {
	nextStep: () => void;
	prevStep: () => void;
	setActiveStepPath: (path: string) => void;
	setIsLoading: (isLoading: boolean) => void;
	isLoading: boolean;
}

const NavigationStepper = forwardRef<
	NavigationStepperRef,
	NavigationStepperProps
>(
	(
		{
			steps,
			onDone,
			initialStepPath,
			canSkip = false,
			renderItems,
			onStepChange,
			showStepNumbers,
			showStepNavigation = true,
			navigationClassName,
		},
		ref,
	) => {
		const [activeStep, setActiveStep] =
			useState<NavigationStepperItemType | null>(null);
		const [activeStepPath, setActiveStepPath] = useState(initialStepPath || '');
		const [isLoading, setIsLoading] = useState(false);

		const {
			findActiveStep,
			findNextStepPath,
			findPrevStepPath,
			transformedSteps,
			routes,
		} = useNavigationStepper({ steps, canSkip, activeKey: activeStepPath });

		useEffect(() => {
			const step = findActiveStep(activeStepPath);
			if (!step) setActiveStepPath(initialStepPath || '');

			setActiveStep(step);
			onStepChange?.(activeStepPath);
		}, [activeStepPath, steps]);

		useEffect(() => {
			setActiveStepPath(initialStepPath || '');
		}, [initialStepPath]);

		useImperativeHandle(ref, () => ({
			setActiveStepPath,
		}));

		const handleStepChange = (step: NavigationStepperItemType) => {
			if (step.disabled) {
				return;
			}

			if ('onClick' in step) {
				step.onClick();
				return;
			}

			if ('onStepChange' in step) {
				step.onStepChange?.();
			}

			setActiveStepPath(step.key);
		};

		const handleNextStep = () => {
			const step = findNextStepPath(activeStepPath);

			if (step) {
				setActiveStepPath(step);
			} else {
				onDone?.();
			}
		};

		const handlePrevStep = () => {
			const step = findPrevStepPath(activeStepPath);

			if (step) {
				setActiveStepPath(step);
			} else {
				onDone?.();
			}
		};

		return (
			<div className={styles.container}>
				{isLoading && <OverlayLoading />}
				{showStepNavigation && (
					<div>
						{renderItems ? (
							renderItems({
								items: transformedSteps,
								activeKey: activeStepPath,
								onChange: handleStepChange,
							})
						) : (
							<NavigationStepperItems
								items={transformedSteps}
								activeKey={activeStepPath}
								onChange={handleStepChange}
								showStepNumbers={showStepNumbers}
								className={navigationClassName}
							/>
						)}
					</div>
				)}
				<div className={styles.componentStep}>
					{!!activeStep && 'component' in activeStep ? (
						activeStep.component({
							nextStep: handleNextStep,
							prevStep: handlePrevStep,
							setActiveStepPath,
							setIsLoading,
							isLoading,
						})
					) : (
						<></>
					)}
				</div>
			</div>
		);
	},
);

export default NavigationStepper;
